import {
  isQuestionRequired,
  isQuestionVisible,
  isSectionVisible,
  type AnswerMap,
  type AnswerValue,
  type SampleEntryValue,
  type SampleField,
  type StoreAttributeKey,
  type TemplateDefinition,
} from './template';

/**
 * Answer-state helpers shared by the renderer and (Phase 2b) the IndexedDB
 * local-first store. All updates are immutable so React state and the sync
 * queue can diff cheaply.
 *
 * Sample groups are dynamic ("verify up to N, minimum M"): the answer array
 * holds exactly the entries the auditor added — no padding.
 */

export function setAnswer(
  answers: AnswerMap,
  questionId: string,
  value: AnswerValue | undefined,
): AnswerMap {
  const next = { ...answers };
  if (value === undefined) delete next[questionId];
  else next[questionId] = value;
  return next;
}

export function getSampleEntries(
  answers: AnswerMap,
  questionId: string,
): SampleEntryValue[] {
  const raw = answers[questionId];
  return Array.isArray(raw) ? (raw as SampleEntryValue[]) : [];
}

export function addSampleEntry(
  answers: AnswerMap,
  questionId: string,
  maxCount: number,
): AnswerMap {
  const entries = getSampleEntries(answers, questionId);
  if (entries.length >= maxCount) return answers;
  return setAnswer(answers, questionId, [...entries, {}]);
}

export function removeSampleEntry(
  answers: AnswerMap,
  questionId: string,
  index: number,
): AnswerMap {
  const entries = getSampleEntries(answers, questionId);
  const next = entries.filter((_, i) => i !== index);
  return setAnswer(answers, questionId, next.length ? next : undefined);
}

export function setSampleField(
  answers: AnswerMap,
  questionId: string,
  index: number,
  fieldId: string,
  value: string | undefined,
): AnswerMap {
  const entries = getSampleEntries(answers, questionId);
  if (index < 0 || index >= entries.length) return answers;
  const entry = { ...entries[index], [fieldId]: value };
  if (value === undefined) delete entry[fieldId];
  const next = [...entries];
  next[index] = entry;
  return setAnswer(answers, questionId, next);
}

/** Tri-state facility attribute: 'unknown' = never captured. */
export type AttributeState = 'yes' | 'no' | 'unknown';

export interface StoreAttributeStates {
  hasParking: AttributeState;
  hasElevators: AttributeState;
}

/** Derive gate-ready attribute states from a store record. */
export function attributeStatesFromStore(store: {
  parking: 'yes' | 'no' | 'unknown';
  elevatorCount: number | null;
}): StoreAttributeStates {
  return {
    hasParking: store.parking,
    hasElevators:
      store.elevatorCount === null
        ? 'unknown'
        : store.elevatorCount > 0
          ? 'yes'
          : 'no',
  };
}

/**
 * Initial answers when starting an audit for a store. Three scenarios per
 * gate (his ruling 2026-08-11):
 *  - attribute known yes/no → gate pre-answered (still editable)
 *  - attribute unknown → gate left blank AND reported in missingAttributes
 *    so the conduct surface can warn "store data incomplete".
 */
export function initialAnswersForStore(
  def: TemplateDefinition,
  attrs: StoreAttributeStates,
): { answers: AnswerMap; missingAttributes: StoreAttributeKey[] } {
  const answers: AnswerMap = {};
  const missing = new Set<StoreAttributeKey>();
  for (const section of def.sections) {
    for (const q of section.questions) {
      const key = q.prefillStoreAttribute;
      if (!key) continue;
      const state = attrs[key];
      if (state === 'unknown') missing.add(key);
      else answers[q.id] = state;
    }
  }
  return { answers, missingAttributes: [...missing] };
}

// ---------------------------------------------------------------------------
// Requiredness & submission validation
// ---------------------------------------------------------------------------

export interface MissingAnswer {
  sectionId: string;
  questionId: string;
  /** Set when the gap is inside a sample-group entry. */
  sampleIndex?: number;
  fieldId?: string;
  /** Set when a sample group has fewer entries than its minCount. */
  needsEntries?: number;
}

function isBlank(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/** Is this field required for THIS entry, honoring requiredIf conditions? */
export function isFieldRequired(
  field: SampleField,
  entry: SampleEntryValue,
): boolean {
  if (!field.requiredIf) return true;
  return entry[field.requiredIf.fieldId] === field.requiredIf.equals;
}

/**
 * Everything a submit must reject on: required, VISIBLE questions without a
 * usable answer. For sample groups: at least minCount entries, and every
 * required field (per requiredIf) of every EXISTING entry. Hidden
 * (gated-off) content is never required.
 */
export function findMissingRequired(
  def: TemplateDefinition,
  answers: AnswerMap,
): MissingAnswer[] {
  const missing: MissingAnswer[] = [];

  for (const section of def.sections) {
    if (!isSectionVisible(def, section, answers)) continue;
    for (const q of section.questions) {
      if (!isQuestionRequired(def, q, answers)) continue;
      if (!isQuestionVisible(def, section, q, answers)) continue;

      if (q.type === 'sample_group') {
        const entries = getSampleEntries(answers, q.id);
        const min = q.minCount ?? 0;
        if (entries.length < min) {
          missing.push({
            sectionId: section.id,
            questionId: q.id,
            needsEntries: min - entries.length,
          });
        }
        entries.forEach((entry, sampleIndex) => {
          for (const field of q.fields ?? []) {
            if (!isFieldRequired(field, entry)) continue;
            if (isBlank(entry[field.id])) {
              missing.push({
                sectionId: section.id,
                questionId: q.id,
                sampleIndex,
                fieldId: field.id,
              });
            }
          }
        });
        continue;
      }

      if (isBlank(answers[q.id])) {
        missing.push({ sectionId: section.id, questionId: q.id });
      }
    }
  }
  return missing;
}

// ---------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------

export interface Progress {
  answered: number;
  total: number;
}

/** Per-section progress for the jump bar; overall = sum. */
export function sectionProgress(
  def: TemplateDefinition,
  answers: AnswerMap,
): Record<string, Progress> {
  const result: Record<string, Progress> = {};

  for (const section of def.sections) {
    const p: Progress = { answered: 0, total: 0 };
    result[section.id] = p;
    if (!isSectionVisible(def, section, answers)) continue;

    for (const q of section.questions) {
      if (!isQuestionRequired(def, q, answers)) continue;
      if (!isQuestionVisible(def, section, q, answers)) continue;

      if (q.type === 'sample_group') {
        const entries = getSampleEntries(answers, q.id);
        const fields = q.fields ?? [];
        for (const entry of entries) {
          for (const field of fields) {
            if (!isFieldRequired(field, entry)) continue;
            p.total += 1;
            if (!isBlank(entry[field.id])) p.answered += 1;
          }
        }
        // Entries still owed toward the minimum count as unconditionally
        // required fields not yet answered.
        const shortfall = Math.max(0, (q.minCount ?? 0) - entries.length);
        if (shortfall > 0) {
          const unconditional = fields.filter((f) => !f.requiredIf).length;
          p.total += shortfall * unconditional;
        }
        continue;
      }

      p.total += 1;
      if (!isBlank(answers[q.id])) p.answered += 1;
    }
  }
  return result;
}

/**
 * Every answer given on what is visible — optional ones too (round 1
 * 2026-10-21: an all-optional DM Visit read "1 of 1 required answers"
 * after ~28 answers). A sample group counts each filled field.
 */
export function answeredCount(def: TemplateDefinition, answers: AnswerMap): number {
  let n = 0;
  for (const section of def.sections) {
    if (!isSectionVisible(def, section, answers)) continue;
    for (const q of section.questions) {
      if (!isQuestionVisible(def, section, q, answers)) continue;
      if (q.type === 'sample_group') {
        for (const entry of getSampleEntries(answers, q.id)) {
          for (const field of q.fields ?? []) if (!isBlank(entry[field.id])) n += 1;
        }
        continue;
      }
      if (!isBlank(answers[q.id])) n += 1;
    }
  }
  return n;
}

export function answerProgress(
  def: TemplateDefinition,
  answers: AnswerMap,
): Progress {
  const sections = sectionProgress(def, answers);
  return Object.values(sections).reduce(
    (acc, p) => ({ answered: acc.answered + p.answered, total: acc.total + p.total }),
    { answered: 0, total: 0 },
  );
}
